import React, {useEffect, useMemo, useState} from "react";
import {Dropdown} from "react-bootstrap";
import MolecularEditorModal from "./MolecularEditorModal";
import molEditorImage from "../img/mol-editor.png";
import SearchTermEditor from "./SearchTermEditor";
import {checkOfflineState} from "./OfflineDetect";
import {inject, observer} from "mobx-react";
import {toJS} from "mobx";
import CopyToClipboardButton from "./CopyToClipboardButton";
import {TAXID_HUMAN, TAXID_MOUSE, TAXID_NONE, TAXID_RAT} from "../stores/AppStatusStore";
import ChatModal from "./modals/ChatModal";
import {Threshold} from "./menus/Threshold";
import Checkbox from "./controls/Checkbox";
import {toast} from "react-toastify";
import HAL9000Eye from "./HAL9000Eye";

import {useModifierKeys} from "../hooks/useModifierKeys";
import { AGENT_ANALYST, useLLMClient } from "../llm/LLMClient";
import {useNavigate} from "react-router-dom";
import SearchContext from "../lib/SearchContext";


const ACTIVITIES = ["*", "*-", "EC50", "ED50", "IC50", "ID50", "Kb", "Kd", "Ki", "Potency", "DC50"];

/**
 * SearchBoxIconLabel
 * Stacks an icon (img or glyphicon) above a small ALL-CAPS text label.
 * Similar to NavBarIconLabel but adapted for SearchBox buttons.
 */
const SearchBoxIconLabel = ({ icon, label, iconType = "glyphicon", imgSrc = null }) => (
  <span className="searchbox-icon-label">
    {iconType === "img" && imgSrc ? (
      <img src={imgSrc} alt="" aria-hidden="true" />
    ) : (
      <span className={'glyphicon ' + icon} aria-hidden="true" />
    )}
    <span className="searchbox-icon-label__text">{label}</span>
  </span>
);

const parseFilter = (xf => {
  if (xf) {
    // <type> <= <threshold> nM
    const parts = xf.split(/\s/);
    if (parts.length === 4 && parts[1] === "<=" && parts[3].toLowerCase() === "nm") {
      const type = parts[0];
      if (new Set(ACTIVITIES.map(s => s.toLowerCase())).has(type.toLowerCase())) {
        try {
          const threshold = parseInt(parts[2]);
          return [type, threshold];
        } catch (e) {
        }
      }
    }
  }
  return [null, null];
});

const SearchButton = (props) => {
  const {children, selectProps, innerProps} = props;
  const {searchProps: {handleRunSearch, isEmpty, isDisabled}} = selectProps;
  return (
    <button
      className={`search-btn${isDisabled ? " disabled" : ""}`}
      onClick={handleRunSearch}
      disabled={isEmpty || isDisabled}
      {...innerProps}
      onMouseDown={e => e.preventDefault()}
      title={isEmpty
        ? "Search terms are required to perform a search"
        : isDisabled
          ? "Change the search terms or one or more search settings to enable a new search"
          : "Submit search with the current terms"}
    >
      <span className="glyphicon glyphicon-search search-icon"/>
      {children}
    </button>
  );
}


const SearchBox = ({
                     className = null,
                     autoFocus = true,
                     searchSessionStore,
                     userStore,
                     appStatusStore,
                     onAIClick = null,
                     aiAvailable = null,
                     aiLabel = "AI",
                     withAutoplex = true,
                     hideAIButton = false,
                   }) => {
  const navigate = useNavigate();
  const {
    modifySearchParameters,
    blankQueuedSearchTerms,
    runNewSearch,
    fetchSuggestions,
    templates,
    datasets,
    hasFullResults = false,
    chatContext,
    resolvingTerms : termsBeingResolved,
    queuedSearchURL,
    queuedSearchParams,
    paramsWithDefaults,
    categories,
    awaitingInitialResults,
    hasNewParams,
    authData,
    resolveSourceLinks,
  } = searchSessionStore;

  // Create searchParams that updates only when clientSearchKey changes
  // Use toJS() to convert MobX observable to plain object without triggering reactions
  const searchParams = useMemo(
    () => toJS(searchSessionStore.params),
    [searchSessionStore.clientSearchKey]
  );
  const client = useLLMClient(AGENT_ANALYST, searchParams, authData, userStore, appStatusStore);

  // Clear conversation when search context changes (new search)
  useEffect(() => {
    client.clear();
  }, [searchSessionStore.clientSearchKey]);
  const {
    queryEntities, xf = null,
    simSubstructure = null
  } = queuedSearchParams;
  const {organismRestriction, aiModels = {}} = appStatusStore;
  const setTargetRestriction = (taxID) => {
    appStatusStore.organismRestriction = taxID;
  }

  if (aiAvailable === null) {
    aiAvailable = Object.keys(aiModels || {}).length > 0;
  }
  const disableAI = !aiAvailable || awaitingInitialResults;
  const {isAdmin} = userStore;

  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [hasOptions, setHasOptions] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [isResolving, setIsResolving] = useState(termsBeingResolved?.length > 0);
  const [showAIModal, setShowAIModal] = useState(false);
  const [inputText, setInputText] = useState("");
  const [textToResolve, setTextToResolve] = useState("");

  const [filterActivityType, filterThreshold] = parseFilter(xf);
  const [activityType, setActivityType] = useState(filterActivityType || "*");
  const [activityThreshold, setActivityThreshold] = useState(filterThreshold || 5000);
  const [useActivityFilter, setUseActivityFilter] = useState(filterActivityType && filterThreshold);
  // TODO: remove versus set empty query parameters; remove gets search defaults,
  // empty forces empty
  // Separate temporary state flag when all items are deleted; if we remove
  const [isEmpty, setIsEmpty] = useState(!termsBeingResolved && queryEntities?.length === 0);

  // Always pass queryEntities to avoid reference changes that cause flashing
  const effectiveSearchTerms = queryEntities || [];

  const toggleAIModal = () => setShowAIModal(!showAIModal);

  useEffect(() => {
    const [type, threshold] = parseFilter(xf);
    if (type && threshold) {
      setActivityType(type);
      setActivityThreshold(threshold);
      setUseActivityFilter(true);
    }
    else {
      //setUseActivityFilter(false);
    }
  }, [xf]);

  useEffect(() => {
    const filter = useActivityFilter ? `${activityType} <= ${activityThreshold} nM` : '';
    if (filter !== xf) {
      modifySearchParameters({xf: filter});
    }
  }, [activityType, activityThreshold, useActivityFilter]);

  const handleResolvingChange = (resolving) => {
    setIsResolving(resolving);
  };
  const handleRunSearch = () => {
    if (!window.navigator.onLine) {
      console.warn("Can't start search: no internet connection");
      return;
    }
    if (queuedSearchURL != null) {
      // Navigate explicitly to the search URL — this creates the browser history entry
      // and moves us off the landing page. The navigation effect in App.js will detect
      // the new URL and call runNewSearch via launchSearchFromURL, which sets
      // awaitingInitialResults before calling runNewSearch.
      const { pathname, search, hash } = queuedSearchURL;
      const searchURLStr = `${pathname}${search}${hash}`;
      navigate(searchURLStr, { state: { newSearch: true, queryEntities: toJS(queryEntities) } });
    }
    else {
      const warnMsg = inputText
        ? hasOptions
          ? "Please make a selection from autosuggest."
          : "Search term not recognized. Please try another term."
        : "Please enter a search term.";

      toast.warning(warnMsg);
    }
  };

  const handleNewSuggestionsObtained = newOptions => {
    setHasOptions(!!newOptions?.length);
  };

  const handleNewSuggestionsFailure = error => {
    toast.error(`Completion options could not be fetched (${error})`);
  };

  useEffect(() => {
    if (termsBeingResolved?.length > 0) {
      setIsEmpty(false);
    }
  }, [termsBeingResolved]);

  const handleSearchTermsChange = newValue => {
    if (newValue?.length) {
      modifySearchParameters({ queryEntities: newValue });
      setIsEmpty(false);
    }
    else {
      // Tell the store too: it kept the last non-empty terms, which a tab switch then put
      // back in the field on their own (PLEX-6631)
      blankQueuedSearchTerms();
      setIsEmpty(true);
    }
    setTextToResolve("");
  };

  const handleInputChange = newInput => {
    setInputText(newInput);
  };

  const handleAddTermToResolve = (term, isQuery = false) => {
    modifySearchParameters({simSubstructure: simSubstructure || isQuery})
    setTextToResolve(term);
  }

  const handleReplaceTerm = (term, isQuery = false) => {
    if (!inputText) {
      // Drop the final query item
      const qe = queryEntities.slice(0, -2);
      const searchIDs = qe.map(x => x.id);
      modifySearchParameters({queryEntities: qe, searchIDs, simSubstructure: simSubstructure || isQuery})
      if (!qe.length) {
        setIsEmpty(true);
      }
    }
    setTextToResolve(term);
  }

  const handleMolecularEditorSearch = (term, isQuery = false) => {
    // Build URL from current settings, replacing search terms with the new molecule.
    // We bypass queuedSearchParams because its searchIDs/queryEntities logic assumes
    // pre-resolved entities; raw smiles:/query: terms need to go in as searchIDs directly.
    const {queryEntities: _, ...baseParams} = toJS(paramsWithDefaults);
    const params = {...baseParams, searchIDs: [term], simSubstructure: simSubstructure || isQuery};
    const url = SearchContext.getAppSearchURL(params, {}, {withLabels: true});
    if (url) navigate(url.pathname + url.search + url.hash, {state: {newSearch: true}});
  };

  const handleEditorClose = () => {
    setShowEditor(false);
  };

  const activeSearchTerm = effectiveSearchTerms?.length === 1 ? effectiveSearchTerms[0] : null;
  const molEditorContent = inputText !== ""
                           ? inputText
                           : activeSearchTerm ? activeSearchTerm[activeSearchTerm.unmapped ? "id" : "smiles"] : "";
  const disableSearch = isEmpty || !hasNewParams || isResolving;
  const disableEditor = false;

  //console.debug(`at=${at} ath=${ath} af=${useActivityFilter}`);
  //console.debug(`xf=${xf} (${searchParams.xf}) me=${minExpansion} (${searchParams.minExpansion})`);

  const modifiers = useModifierKeys();
  const showRemainingOptions = !(modifiers.shift || modifiers.alt);

  const now = new Date();
  const foundersDay = now.getMonth() === 3 && now.getDate() === 1;
  if (foundersDay) {
    aiLabel = (<HAL9000Eye />);
  }

  return (
    <div
      className={`search-box${className ? " " + className : ""}`}
    >
      <SearchTermEditor
        autoFocus={autoFocus}
        fetchSuggestions={fetchSuggestions}
        onValueChange={handleSearchTermsChange}
        onNewOptionsObtained={handleNewSuggestionsObtained}
        onNewOptionsFailure={handleNewSuggestionsFailure}
        onInputChange={handleInputChange}
        onRunNewSearch={handleRunSearch}
        onResolvingChange={handleResolvingChange}
        defaultSearchTerms={queryEntities}
        searchTerms={effectiveSearchTerms}
        termsBeingResolved={termsBeingResolved}
        textToResolve={textToResolve}
        searchButton={SearchButton}
        searchProps={{isEmpty, handleRunSearch, isDisabled: disableSearch}}
        showRemainingOptions={showRemainingOptions}
        templates={templates}
        title={!isEmpty ? "Start search on current terms" : "Enter one or more terms to search"}
        />
      <button
        className="mol-editor-dlg-btn"
        onClick={checkOfflineState(_ => {
          // Clear input text when opening editor - if it's not a valid compound encoding,
          // the editor will open empty and we don't want non-compound text lingering
          if (inputText) {
            setInputText("");
          }
          setShowEditor(true);
        })}
        title="Open chemical structure editor"
        disabled={disableEditor}
      >
        <SearchBoxIconLabel iconType="img" imgSrc={molEditorImage} label="Struct" />
      </button>
      <Dropdown
        title="Target Lookup Filtering"
        className="dropdown-target-restriction"
        key="target-restriction"
        onToggle={(isOpen) => setShowFilterDropdown(isOpen)}
        show={showFilterDropdown}
      >
        <Dropdown.Toggle><SearchBoxIconLabel icon="glyphicon-filter" label="Filter" /></Dropdown.Toggle>
        <Dropdown.Menu>
          <Threshold
            className={"activity-filter toggle"}
            title={"Filter for at least one activity measurement meeting this criteria"}
            onEnter={() => setShowFilterDropdown(false)}
            checkType={"checkbox"}
            label={(<span>
              Activity
              <select
                value={activityType}
                onChange={(e) => setActivityType(e.target.value)}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation()
                }}
              >
                {ACTIVITIES.sort().map(el => (<option key={el} value={el}>{el === "*" ? "Any" : el === "*-" ? "Any (strict)" : el}</option>))}
              </select> &lt;=
            </span>)}
            min={1} step={100} precision={null}
            value={activityThreshold}
            isSelected={useActivityFilter}
            onValueChange={value => setActivityThreshold(value)}
            onToggle={checked => setUseActivityFilter(checked)}
          ><span> nM</span></Threshold>
          <Dropdown.Header>Target Lookup Restriction</Dropdown.Header>
          <Dropdown.Item
            onClick={() => setTargetRestriction(organismRestriction === TAXID_HUMAN ? TAXID_NONE : TAXID_HUMAN)}
            title="Restrict target suggestions to this species only"
            key="human"
          >
            <Checkbox
              checked={organismRestriction === TAXID_HUMAN}
              label={"Homo Sapiens"}
              title="Restrict target suggestions to this species only"
              onChange={() => setTargetRestriction(organismRestriction === TAXID_HUMAN ? TAXID_NONE : TAXID_HUMAN)}
            />
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => setTargetRestriction(organismRestriction === TAXID_MOUSE ? TAXID_NONE : TAXID_MOUSE)}
            title="Restrict target suggestions to this species only"
            key="mouse"
          >
            <Checkbox
              checked={organismRestriction === TAXID_MOUSE}
              label={"Mus Musculus"}
              title="Restrict target suggestions to this species only"
              onChange={() => setTargetRestriction(organismRestriction === TAXID_MOUSE ? TAXID_NONE : TAXID_MOUSE)}
            />
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => setTargetRestriction(organismRestriction === TAXID_RAT ? TAXID_NONE : TAXID_RAT)}
            title="Restrict target suggestions to this species only"
            key="rat"
          >
            <Checkbox
              checked={organismRestriction === TAXID_RAT}
              label={"Rattus Norvegicus"}
              title="Restrict target suggestions to this species only"
              onChange={() => setTargetRestriction(organismRestriction === TAXID_RAT ? TAXID_NONE : TAXID_RAT)}
            />
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      {isAdmin &&
        (<CopyToClipboardButton
          className="admin"
          title={hasFullResults ? "Copy all search results to clipboard" : "Wait for search results to finish loading..."}
          disabled={!hasFullResults}
          getText={() => searchSessionStore.clipboardText}
          label="Copy"
        />)}
      {!hideAIButton && (
        <button
          className={"chat"}
          disabled={disableAI}
          title={foundersDay ? "What are you doing, Dave?"
                  : onAIClick
                  ? `Switch to chat mode${withAutoplex ? " (autoplex)" : " - ask about Plex features and help"}`
                  : disableAI ? "Chat unavailable" : "Discuss these results with the Plex LLM"}
          onClick={onAIClick ? onAIClick : toggleAIModal}
        >
          <span className="searchbox-icon-label">
            {foundersDay
              ? aiLabel
              : <><span className="glyphicon glyphicon-comment" aria-hidden="true" /><span className="searchbox-icon-label__text">AI Assist</span></>
            }
          </span>
        </button>
      )}
      <ChatModal
        key={searchSessionStore.clientSearchKey}
        client={client}
        isOpen={showAIModal}
        onHide={toggleAIModal}
        initialInput={chatContext}
        auth={authData}
        sessionAgent={AGENT_ANALYST}
        searchParams={searchParams}
        searchContextKey={searchSessionStore.clientSearchKey}
        historyId={searchSessionStore.currentSearchConversationId}
        historyType="search"
        categories={categories}
        resolveSourceLinks={resolveSourceLinks}
        waitingForResults={awaitingInitialResults}
      />
      <MolecularEditorModal
        showEditor={showEditor}
        onCloseEditor={handleEditorClose}
        encoding={molEditorContent}
        onSearchFromEditor={handleMolecularEditorSearch}
        onAddTerm={handleAddTermToResolve}
        onReplaceTerm={handleReplaceTerm}
        forceSubstructureQuery={simSubstructure}
      />
    </div>
  );
};

export default inject("appStatusStore", "userStore", "searchSessionStore")(observer(SearchBox));
